// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import { useReactiveVar, useMutation, useQuery } from '@apollo/client';
import { pathOr, difference } from 'ramda';
import { useDispatch } from 'react-redux';
import ApolloLightbox from '../apollo-lightbox';
import Button from '../../basic-components/button';
import Icon from '../../basic-components/icon';
import ProductsTableView from './products-table-view';
import ApplyChangesConfirmation from './apply-changes-confirmation';
import {
  aiModalChangesVar,
  aiModalChangeSuccessIdsVar,
  aiModalChangeErrorIdsVar,
} from '../../utils/apollo-cache';
import { ApplyChanges } from '../../shared-queries/ApplyChanges.graphql';
import { ActivateShowcasesAndPublish } from '../../shared-queries/ActivateShowcasesAndPublish.graphql';
import { StockData } from '../../shared-queries/StockData.graphql';
import { Products as ProductsQuery } from '../../shared-queries/Products.graphql';
import { formatChanges } from '../stock-table/change-actions/utils';
import { openHttpErrorAlert, openSuccessToast } from '../../actions/shipping';
import Loader from '../../basic-components/loader';
import cs from './styles.pcss';
import t from './locale';

const cx = classNames.bind(cs);

const PAGE_SIZE = 20;

type Props = {
  queryParams: { [string]: string },
  selectedProductId: ?string,
  onClose: () => void,
  fallbackProducts?: Array<any>,
};

export default function AiProductsModal({
  queryParams,
  selectedProductId,
  onClose,
  fallbackProducts,
}: Props): React.Node {
  const dispatch = useDispatch();

  // Build GraphQL filters from queryParams, excluding frontend-only fields
  const filters = React.useMemo(() => {
    const result = {};
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        if (key !== 'publishToSiteId') {
          result[key] = queryParams[key];
        }
      });
    }
    return result;
  }, [queryParams]);

  // Fetch products from API using same query as stock page
  const {
    data: productsData,
    loading: loadingProducts,
    error: productsError,
    fetchMore,
  } = useQuery(ProductsQuery, {
    variables: { first: PAGE_SIZE, filters },
    fetchPolicy: 'no-cache',
  });

  const apiProducts = pathOr([], ['products', 'nodes'], productsData);
  const firstPageInfo = pathOr({}, ['products', 'pageInfo'], productsData);

  // Pages 2+ are kept in component state, NOT in Apollo cache.
  // This prevents updateQuery from polluting the first-page cache key,
  // which would cause stale accumulated data to appear on the next modal open.
  const [additionalProducts, setAdditionalProducts] = React.useState([]);
  const [currentPageInfo, setCurrentPageInfo] = React.useState(null);

  const effectivePageInfo = currentPageInfo || firstPageInfo;
  const hasNextPage = effectivePageInfo.hasNextPage || false;
  const loading = loadingProducts;

  // Reset accumulated pages whenever a fresh first-page response arrives.
  const lastProductsDataRef = React.useRef(null);
  React.useEffect(() => {
    if (!productsData || productsData === lastProductsDataRef.current) return;
    lastProductsDataRef.current = productsData;
    setAdditionalProducts([]);
    setCurrentPageInfo(null);
    lastFetchCursorRef.current = null;
  }, [productsData]);

  const allLoadedProducts = React.useMemo(
    () => [...apiProducts, ...additionalProducts],
    [apiProducts, additionalProducts],
  );

  // Use fallbackProducts when API request fails
  const products = productsError ? fallbackProducts || [] : allLoadedProducts;

  const loadMore = React.useCallback(() => {
    const cursor = effectivePageInfo.endCursor;

    if (
      !hasNextPage ||
      loadingProducts ||
      !cursor ||
      isFetchingMoreRef.current ||
      lastFetchCursorRef.current === cursor
    ) {
      return Promise.resolve();
    }

    isFetchingMoreRef.current = true;
    lastFetchCursorRef.current = cursor;

    return fetchMore({
      variables: {
        first: PAGE_SIZE,
        after: cursor,
        filters,
      },
    })
      .then(({ data: fetchMoreData }) => {
        const newNodes = pathOr([], ['products', 'nodes'], fetchMoreData);
        const newPageInfo = pathOr(null, ['products', 'pageInfo'], fetchMoreData);
        if (newNodes.length > 0) {
          setAdditionalProducts((prev) => [...prev, ...newNodes]);
        }
        if (newPageInfo) {
          setCurrentPageInfo(newPageInfo);
        }
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [hasNextPage, loadingProducts, fetchMore, effectivePageInfo.endCursor, filters]);

  // Fetch real stock data (showcases and shipping profiles)
  const { data: stockData, loading: loadingStockData } = useQuery(StockData);

  // Get showcases and shipping profiles from real API
  const showcases = pathOr([], ['showcases'], stockData);
  const shippingProfiles = pathOr([], ['profiles', 'nodes'], stockData);

  // 选中的产品IDs
  const [selectedProducts, setSelectedProducts] = React.useState<Set<string>>(
    new Set(),
  );

  // 选中的变体IDs
  const [selectedVariations, setSelectedVariations] = React.useState<
    Set<string>,
  >(new Set());
  const [selectAllActive, setSelectAllActive] = React.useState(false);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const isFetchingMoreRef = React.useRef(false);
  const lastFetchCursorRef = React.useRef<?string>(null);
  const [confirmationData, setConfirmationData] = React.useState<{
    showcasesToTurnOn: string[],
    showcaseData: Array<{ name: string, code: string }>,
    formattedChanges?: any,
  }>({ showcasesToTurnOn: [], showcaseData: [], formattedChanges: null });

  // Get user data from StockData query
  const registrationDate = pathOr(
    undefined,
    ['viewer', 'createdAt'],
    stockData,
  );
  const managedPayments = true; // Default to true for now
  const limits = pathOr([], ['viewer', 'limits'], stockData);

  // 使用独立的 Apollo reactive var，避免影响 Stock 页面
  const changes = useReactiveVar(aiModalChangesVar);
  const changeSuccessIds = useReactiveVar(aiModalChangeSuccessIdsVar);
  const changeErrorIds = useReactiveVar(aiModalChangeErrorIdsVar);

  // 获取激活的站点 IDs
  const activeShowcases = React.useMemo(
    () => showcases.filter((s) => s.active),
    [showcases],
  );
  const activeShowcasesIds = React.useMemo(
    () => activeShowcases.map((s) => s.id),
    [activeShowcases],
  );

  // 初始化 mutations
  const [applyChanges] = useMutation(ApplyChanges, {
    refetchQueries: ['Products'],
  });

  const [applyChangesWithShowcasesActivation] = useMutation(
    ActivateShowcasesAndPublish,
    { refetchQueries: ['Products'] },
  );

  // 计算当前 modal 中的产品有多少变更
  const modalProductIds = React.useMemo(
    () => new Set(products.map((p) => p.id)),
    [products],
  );

  // 组件挂载时初始化本地状态，卸载时清除
  React.useEffect(
    () => () => {
      // 清除本地状态
      aiModalChangesVar([]);
      aiModalChangeSuccessIdsVar([]);
      aiModalChangeErrorIdsVar([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // 保存每个 product×showcase 的原始 backend 值，用于跨操作比对
  // key: `${productId}|${showcaseId}`，value: 原始 listing.selected
  const [originalValues, setOriginalValues] = React.useState(() => new Map());

  // publishToSiteId site ID 列表 → showcase 对象列表
  const targetShowcases = React.useMemo(() => {
    if (!queryParams?.publishToSiteId || showcases.length === 0) return [];
    const targetSiteIds = Array.isArray(queryParams.publishToSiteId)
      ? queryParams.publishToSiteId
      : [queryParams.publishToSiteId];
    return targetSiteIds
      .map((siteId) =>
        showcases.find((s) => String(s.site?.id) === String(siteId)),
      )
      .filter(Boolean);
  }, [queryParams, showcases]);

  // 当 products/showcases 加载或 loadMore 后，处理新增 products：
  // 1. 记录原始值（用于 effectiveModalChanges 比对）
  // 2. 写入 aiModalChangesVar（相当于用户初始操作）
  // 3. 自动勾选 checkbox
  const processedProductIds = React.useRef(new Set());
  React.useEffect(() => {
    if (allLoadedProducts.length === 0 || showcases.length === 0) return;

    const unprocessed = allLoadedProducts.filter(
      (p) => !processedProductIds.current.has(p.id),
    );
    if (unprocessed.length === 0) return;

    unprocessed.forEach((p) => processedProductIds.current.add(p.id));

    // 1. 记录所有 showcase 的原始值（不只是 publishToSiteId 的站点）
    setOriginalValues((prev) => {
      const updated = new Map(prev);
      unprocessed.forEach((product) => {
        showcases.forEach(({ id: showcaseId, site }) => {
          const key = `${product.id}|${showcaseId}`;
          if (updated.has(key)) return;
          // 与 product-row.js 保持一致的匹配顺序
          let listing = product.listings?.find(
            (l) => l.showcase && l.showcase.id === showcaseId,
          );
          if (!listing && site && site.id !== undefined) {
            listing = product.listings?.find(
              (l) => l.site && l.site.id === site.id,
            );
          }
          updated.set(key, listing?.selected ?? false);
        });
      });
      return updated;
    });

    // 2. 对 publishToSiteId 匹配的站点写入初始操作变更
    if (targetShowcases.length > 0) {
      const newChanges = [];
      unprocessed.forEach((product) => {
        targetShowcases.forEach(({ id: showcaseId, site }) => {
          let listing = product.listings?.find(
            (l) => l.showcase && l.showcase.id === showcaseId,
          );
          if (!listing && site && site.id !== undefined) {
            listing = product.listings?.find(
              (l) => l.site && l.site.id === site.id,
            );
          }
          const currentSelected = listing?.selected ?? false;
          if (currentSelected !== true) {
            newChanges.push({
              productId: product.id,
              variationId: '',
              showcaseId,
              fieldName: 'selected',
              value: true,
              initialValue: currentSelected,
              id: `${product.id}||${showcaseId}|selected`,
              ignore: false,
            });
          }
        });
      });
      if (newChanges.length > 0) {
        aiModalChangesVar([...aiModalChangesVar(), ...newChanges]);
      }
    }

    // 3. 自动勾选 checkbox（functional update 追加）
    if (targetShowcases.length > 0 || selectAllActive) {
      const newProductIds = new Set(unprocessed.map((p) => p.id));
      setSelectedProducts((prev) => new Set([...prev, ...newProductIds]));

      // 只对真正的多变体商品勾选 variations，避免简单商品的 pseudo-variation 被计入 mass edit 数量
      const variationIds = new Set();
      unprocessed.forEach((product) => {
        if (product.variations && product.variations.length > 1) {
          product.variations.forEach((v) => variationIds.add(v.id));
        }
      });
      if (variationIds.size > 0) {
        setSelectedVariations((prev) => new Set([...prev, ...variationIds]));
      }
    }
  }, [allLoadedProducts, showcases, targetShowcases, selectAllActive]);

  // 过滤出只属于当前 modal 产品的变更（排除 ignore 和 mass 行）
  const modalChanges = React.useMemo(
    () =>
      changes.filter(
        (change) =>
          !change.ignore &&
          change.productId !== 'mass' &&
          modalProductIds.has(change.productId),
      ),
    [changes, modalProductIds],
  );

  // 基于原始值的有效变更：当前值 !== 原始 backend 值才算真正变更
  // 这样 mass edit 把值改回原始值时，Apply changes 自动消失
  const effectiveModalChanges = React.useMemo(
    () =>
      modalChanges.filter((change) => {
        const key = `${change.productId}|${change.showcaseId}`;
        const original = originalValues.get(key);
        return original === undefined ? true : change.value !== original;
      }),
    [modalChanges, originalValues],
  );

  const hasChanges = effectiveModalChanges.length > 0;

  // 统计有效变更涉及的产品数量（去重）
  const changedProductsCount = React.useMemo(() => {
    const ids = new Set(effectiveModalChanges.map((c) => c.productId));
    return ids.size;
  }, [effectiveModalChanges]);

  // 处理checkbox选择
  const handleProductSelect = (
    productId: string,
    checked: boolean,
    preserveSelectAll: ?boolean = false,
  ) => {
    if (!preserveSelectAll) {
      setSelectAllActive(false);
    }

    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(productId);
      } else {
        newSet.delete(productId);
      }
      return newSet;
    });

    // 同时处理该商品的所有变体
    const product = products.find((p) => p.id === productId);
    if (product?.variations && product.variations.length > 1) {
      setSelectedVariations((prevVariations) => {
        const newVariations = new Set(prevVariations);
        product.variations.forEach((variation) => {
          if (checked) {
            // 选择主商品时，自动选择所有变体
            newVariations.add(variation.id);
          } else {
            // 取消主商品时，自动取消所有变体
            newVariations.delete(variation.id);
          }
        });
        return newVariations;
      });
    }
  };

  // 处理变体checkbox选择
  const handleVariationToggle = (variationId: string) => {
    setSelectAllActive(false);

    setSelectedVariations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const handleToggleAllProducts = React.useCallback((checked: boolean) => {
    setSelectAllActive(checked);
  }, []);

  // 实际提交修改的逻辑
  const performApplyChanges = async (
    showcasesChange: string[],
    formattedChanges: any,
  ) => {
    setIsSubmitting(true);

    try {
      let result;

      if (showcasesChange.length > 0) {
        // 需要激活站点：使用 ActivateShowcasesAndPublish mutation
        result = await applyChangesWithShowcasesActivation({
          variables: {
            showcaseInput: {
              ids: showcasesChange,
            },
            massUpdate: {
              filters: {},
              changes: formattedChanges,
              endCursor: '',
            },
          },
        });
      } else {
        // 不需要激活站点：使用普通的 ApplyChanges mutation
        result = await applyChanges({
          variables: {
            input: {
              filters: {},
              changes: formattedChanges,
              endCursor: '',
            },
          },
        });
      }

      // 检查结果
      const success = pathOr(
        false,
        ['data', 'productMassUpdate', 'success'],
        result,
      );
      const errors = pathOr(
        [],
        ['data', 'productMassUpdate', 'errors'],
        result,
      );

      if (success) {
        // 清除已应用的变更（只清理本地状态）
        aiModalChangesVar([]);

        // 标记成功的产品
        const changedProductIds = Array.from(
          new Set(effectiveModalChanges.map((c) => c.productId)),
        );
        aiModalChangeSuccessIdsVar([...changeSuccessIds, ...changedProductIds]);

        // 清除选择状态，回到初始状态
        setSelectedProducts(new Set());
        setSelectedVariations(new Set());

        // 使用项目通用的成功提示
        dispatch(openSuccessToast(3000));

        // 不关闭 modal，让用户可以继续操作其他产品
        // 如果需要关闭，用户可以点击 Cancel 按钮
      } else {
        // 标记失败的产品
        const changedProductIds = Array.from(
          new Set(effectiveModalChanges.map((c) => c.productId)),
        );
        aiModalChangeErrorIdsVar([...changeErrorIds, ...changedProductIds]);

        // 使用项目通用的错误提示
        dispatch(
          openHttpErrorAlert(
            errors.length > 0
              ? `Failed to apply changes: ${errors.join(', ')}`
              : 'Failed to apply changes. Please try again.',
          ),
        );
      }
    } catch (error) {
      // 标记失败的产品
      const changedProductIds = Array.from(
        new Set(effectiveModalChanges.map((c) => c.productId)),
      );
      aiModalChangeErrorIdsVar([...changeErrorIds, ...changedProductIds]);

      // 使用项目通用的错误提示
      dispatch(
        openHttpErrorAlert(
          error?.message || 'Failed to apply changes. Please try again.',
        ),
      );
    } finally {
      setIsSubmitting(false);
      setShowConfirmation(false);
    }
  };

  // 点击 Apply 按钮时，先显示确认对话框（如果需要）
  const handleApplyChanges = async () => {
    try {
      // 使用与 Stock Table 相同的 formatChanges 函数格式化变更
      // 注意：AI Products Modal 只支持 selected 字段，不支持 quantity 和 shippingProfileId
      const { formattedChanges, showcasesToTurnOn } = formatChanges(
        effectiveModalChanges,
        products,
        activeShowcasesIds,
        false, // AI Products Modal 不使用批量更新（checkedAll）
        {}, // 不使用过滤器
      );

      // 检测是否需要激活站点
      const showcasesChange = difference(showcasesToTurnOn, activeShowcasesIds);

      // 获取要激活的站点信息（包括名称和代码）
      const showcaseData = showcasesChange.map((id) => {
        const showcase = showcases.find((s) => s.id === id);
        return {
          name: showcase?.name || showcase?.site?.domain || id,
          code: showcase?.site?.code || '',
        };
      });

      // 判断是否需要显示确认对话框
      const shouldShowConfirmation =
        changedProductsCount > 5 || showcasesChange.length > 0;

      if (shouldShowConfirmation) {
        // 保存格式化后的数据，用于确认后提交
        setConfirmationData({
          showcasesToTurnOn: showcasesChange,
          showcaseData,
          formattedChanges, // 保存格式化后的变更
        });
        setShowConfirmation(true);
      } else {
        // 直接提交
        await performApplyChanges(showcasesChange, formattedChanges);
      }
    } catch (error) {
      dispatch(
        openHttpErrorAlert(
          error?.message || 'Failed to prepare changes. Please try again.',
        ),
      );
    }
  };

  // 确认对话框的确认按钮
  const handleConfirmApply = async () => {
    try {
      // 使用保存的格式化数据
      await performApplyChanges(
        confirmationData.showcasesToTurnOn,
        confirmationData.formattedChanges,
      );
    } catch (error) {
      setShowConfirmation(false);
    }
  };

  // 确认对话框的取消按钮
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setConfirmationData({
      showcasesToTurnOn: [],
      showcaseData: [],
      formattedChanges: null,
    });
  };

  // 清除当前 modal 的所有变更
  const handleDiscardChanges = () => {
    // 清除本地状态
    aiModalChangesVar([]);
  };

  const renderFooter = React.useCallback(
    (closeOverlay: () => Promise<void>) => {
      const handleCancel = async () => {
        // 直接清除变更并关闭弹窗，不显示确认提示
        if (hasChanges) {
          handleDiscardChanges();
        }
        await closeOverlay();
      };

      // 计算选中的产品数量
      const totalSelected = selectedProducts.size;

      // 根据是否有选中产品，显示不同的按钮文案
      const applyButtonText =
        totalSelected > 0
          ? t('applyToSelected', { count: totalSelected })
          : t('apply', { count: changedProductsCount });

      return (
        <div className={cx('footer')}>
          <div className={cx('footer-left')}>
            <Button
              mode="secondary"
              size="medium"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
          </div>
          <div className={cx('footer-center')}>
            {hasChanges && (
              <Button
                mode="main"
                size="medium"
                onClick={handleApplyChanges}
                loading={isSubmitting}
                className={cx('apply-button')}
              >
                <Icon name="publishIcon" className={cx('apply-icon')} />
                {applyButtonText}
              </Button>
            )}
          </div>
          <div className={cx('footer-right')} />
        </div>
      );
    },
    [
      hasChanges,
      isSubmitting,
      selectedProducts,
      changedProductsCount,
      handleDiscardChanges,
    ],
  );

  // 显示加载状态（API 报错时跳过，直接展示 fallback）
  if (
    !productsError &&
    (loadingStockData || (loadingProducts && products.length === 0))
  ) {
    return (
      <ApolloLightbox
        id="ai-products-table-modal"
        onClose={onClose}
        header={t('header')}
        className={cx('ai-products-modal')}
      >
        <div className={cx('modal-content', 'loading-state')}>
          <Loader size={42} />
        </div>
      </ApolloLightbox>
    );
  }

  return (
    <>
      <ApolloLightbox
        id="ai-products-table-modal"
        onClose={onClose}
        header={t('header')}
        footer={renderFooter}
        className={cx('ai-products-modal')}
      >
        <div className={cx('modal-content')}>
          <ProductsTableView
            products={products}
            selectedProductId={selectedProductId}
            showcases={showcases}
            selectedProducts={selectedProducts}
            onProductSelect={handleProductSelect}
            onToggleAllProducts={handleToggleAllProducts}
            shippingProfiles={shippingProfiles}
            registrationDate={registrationDate}
            managedPayments={managedPayments}
            limits={limits}
            checkedVariations={selectedVariations}
            onVariationToggle={handleVariationToggle}
            onLoadMore={loadMore}
            hasNextPage={hasNextPage}
            loading={loading}
            changes={changes}
          />
        </div>
      </ApolloLightbox>

      {/* 确认对话框 */}
      <ApplyChangesConfirmation
        opened={showConfirmation}
        changedProductsCount={changedProductsCount}
        showcasesToActivateCount={confirmationData.showcasesToTurnOn.length}
        showcaseData={confirmationData.showcaseData}
        onConfirm={handleConfirmApply}
        onCancel={handleCancelConfirmation}
        loading={isSubmitting}
      />
    </>
  );
}
